// [AUTH_GOOGLE_V1] Utility Helpers for PWA and Auth Stability

export const isIOS = () => {
    if (typeof window === 'undefined') return false;
    const isStandardIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    // Check for iPadOS (MacIntel + touchpoints)
    const isIPadOS = (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    return isStandardIOS || isIPadOS;
};

export const isPWA = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || document.referrer.includes('android-app://');
};

// --- storageSafe Logic ---

export const safeSet = (key, val) => {
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(key, val);
        } else if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(key, val);
        }
    } catch (e) {
        console.warn("[AUTH_GOOGLE] safeSet failed", e);
    }
};

export const safeGet = (key) => {
    try {
        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem(key);
        } else if (typeof sessionStorage !== 'undefined') {
            return sessionStorage.getItem(key);
        }
    } catch (e) {
        return null;
    }
    return null;
};

export const safeRemove = (key) => {
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(key);
        }
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(key);
        }
    } catch (e) {
        // no-op
    }
};

// --- TTL Logic ---

export const setLocalWithTTL = (key, value, ttlMs) => {
    const item = {
        value: value,
        expiry: Date.now() + ttlMs,
    };
    safeSet(key, JSON.stringify(item));
};

export const getLocalWithTTL = (key) => {
    const itemStr = safeGet(key);
    if (!itemStr) return null;
    try {
        const item = JSON.parse(itemStr);
        if (Date.now() > item.expiry) {
            safeRemove(key);
            return null;
        }
        return item.value;
    } catch (e) {
        safeRemove(key);
        return null;
    }
};

// --- Auth Logs Ring Buffer ---

export const logAuth = (msg, data = null) => {
    const timestamp = new Date().toISOString();
    const logEntry = { ts: timestamp, msg: msg, data: data };

    // Add to window buffer (volatile)
    if (typeof window !== 'undefined') {
        if (!window._AUTH_LOGS_) window._AUTH_LOGS_ = [];
        window._AUTH_LOGS_.push(logEntry);
        if (window._AUTH_LOGS_.length > 50) window._AUTH_LOGS_.shift();
    }

    // Persist to localStorage with TTL 30min
    try {
        const currentLogsStr = safeGet("authDebugLogs");
        let currentLogs = [];
        if (currentLogsStr) {
            const parsed = JSON.parse(currentLogsStr);
            // Check expiry manually or via helper
            if (Date.now() < parsed.expiry) {
                currentLogs = parsed.value;
            }
        }
        currentLogs.push(logEntry);
        if (currentLogs.length > 50) currentLogs.shift();

        setLocalWithTTL("authDebugLogs", currentLogs, 30 * 60 * 1000); // 30 min
    } catch (e) {
        // Silent fail
    }

    //console.log(`[AUTH_GOOGLE] ${msg}`, data || "");
};

/**
 * [V2.0.0] Shared Validation Utilities
 */

export const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
};

/**
 * Normalizes phone numbers to E.164 format
 * @param {string} input - Raw phone input
 * @param {string} countryPhoneCode - Default country code (e.g. "33")
 */
export const normalizePhoneToE164 = (input, countryPhoneCode) => {
    if (!input) return null;
    let clean = String(input).replace(/[\s.-]/g, "");
    
    if (clean.startsWith("00")) clean = "+" + clean.substring(2);

    // Special handling for FR (33)
    if (countryPhoneCode === "33" && clean.startsWith("0") && clean.length === 10) {
        clean = "+33" + clean.substring(1);
    } else if (!clean.startsWith("+")) {
        clean = "+" + countryPhoneCode + clean;
    }

    // Strict E.164 Regex validation
    const e164Regex = /^\+[1-9]\d{7,14}$/;
    return e164Regex.test(clean) ? clean : null;
};

/**
 * Positive whitelist check: determine if a booking represents a driver ride actively in progress.
 */
export const isDriverRideInProgress = (booking, nowMs = Date.now()) => {
    if (!booking || typeof booking !== 'object') return false;
    const status = String(booking.status || '').toUpperCase().trim();
    const tripStatus = String(booking.trip_status || '').toUpperCase().trim();

    // 0. Completed, cancelled, paid or terminated bookings are NEVER in progress
    const terminalStatuses = ['COMPLETE', 'COMPLETED', 'PAID', 'CANCELLED', 'REJECTED', 'EXPIRED', 'PENDING_PAYMENT_FAILED'];
    if (terminalStatuses.includes(status) || terminalStatuses.includes(tripStatus)) {
        return false;
    }

    // 1. Course engagée / en cours d'exécution
    if (['STARTED', 'ARRIVED'].includes(status)) return true;
    if (['DRIVER_ON_THE_WAY', 'ARRIVED', 'STARTED', 'REACHED'].includes(tripStatus)) return true;

    // 2. Réservation immédiate acceptée
    if (status === 'ACCEPTED') {
        const isImmediate = booking.bookLater === false || (
            booking.bookLater !== true &&
            (!booking.bookingDate || isNaN(Date.parse(booking.bookingDate)))
        );
        if (isImmediate) return true;
    }
    return false;
};

/**
 * Find active rides for a driver and detect conflicts.
 */
export const findDriverActiveRide = (bookings, driverUid, nowMs = Date.now()) => {
    if (!bookings || !driverUid) return { activeRides: [], activeRide: null, hasConflict: false };
    const list = Array.isArray(bookings) ? bookings : Object.values(bookings);
    const activeRides = [];
    for (const b of list) {
        if (!b || typeof b !== 'object') continue;
        const isAssigned = (
            b.driver === driverUid ||
            b.driverId === driverUid ||
            b.driverUid === driverUid ||
            b.driver_uid === driverUid ||
            b.driver?.uid === driverUid
        );
        if (isAssigned && isDriverRideInProgress(b, nowMs)) {
            activeRides.push(b);
        }
    }
    return {
        activeRides,
        activeRide: activeRides.length === 1 ? activeRides[0] : null,
        hasConflict: activeRides.length > 1
    };
};

/**
 * Determine the exact operational service status of a vehicle for admin consultation:
 * - 'Hors ligne'
 * - 'En ligne (libre)'
 * - 'En ligne (en course)'
 */
export const getVehicleServiceStatus = (car, driverUser, bookings = [], availableDrivers = null) => {
    if (!car || !car.id) return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };

    // 0. Car must be approved to be in service
    if (!car.approved && !car.carApproved) {
        return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };
    }

    // 1. Check if driver exists and is approved / active
    if (!driverUser || typeof driverUser !== 'object') {
        return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };
    }

    const driverUid = driverUser.uid || driverUser.id || car.driverId || car.driver;

    // 1a. If the live available drivers pool is provided (e.g. from fetchDrivers / usersdata.drivers),
    // cross-reference it: a driver MUST be in the active drivers pool to have a vehicle in service.
    if (Array.isArray(availableDrivers)) {
        const isDriverInPool = availableDrivers.some(d => d && (
            (d.id && String(d.id) === String(driverUid)) ||
            (d.uid && String(d.uid) === String(driverUid))
        ));
        if (!isDriverInPool) {
            return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };
        }
    }

    // 1b. If driver has no live location in RTDB (locations[uid] was missing so fetchUsersOnce set user.location = null),
    // they are not connected/on-duty.
    if (driverUser.location === null) {
        return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };
    }

    const isDriverOnline = Boolean(
        driverUser.driverActiveStatus === true &&
        driverUser.approved === true &&
        !driverUser.accountDeleted &&
        !driverUser.isDeleted &&
        driverUser.isActive !== false &&
        driverUser.activeMode !== 'customer'
    );
    if (!isDriverOnline) {
        return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };
    }

    // 2. Check if this specific car is the driver's active on-duty vehicle
    let isVehicleInService = false;
    if (driverUser.activeCarId) {
        isVehicleInService = String(driverUser.activeCarId) === String(car.id);
    } else if (driverUser.carType) {
        isVehicleInService = String(driverUser.carType).trim().toLowerCase() === String(car.carType || '').trim().toLowerCase();
    } else {
        isVehicleInService = car.active === true;
    }
    if (!isVehicleInService) {
        return { status: 'OFFLINE', label: 'Hors ligne', color: 'offline' };
    }

    // 3. Check if driver is currently in a ride
    const isQueue = Boolean(driverUser.queue);
    const hasRideInProgress = (Array.isArray(bookings) ? bookings : Object.values(bookings || {})).some(b => {
        if (!b || typeof b !== 'object') return false;
        const isAssigned = (
            b.driver === driverUid ||
            b.driverId === driverUid ||
            b.driverUid === driverUid ||
            b.driver_uid === driverUid ||
            b.driver?.uid === driverUid
        );
        if (!isAssigned) return false;
        return isDriverRideInProgress(b);
    });

    if (isQueue || hasRideInProgress) {
        return { status: 'ON_DUTY_BUSY', label: 'En ligne (en course)', color: 'busy' };
    }

    return { status: 'ON_DUTY_FREE', label: 'En ligne (libre)', color: 'free' };
};
